import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus, Car, Home, Smartphone, Package, Eye, Edit2,
    Trash2, CheckCircle, XCircle, Clock, Loader2, Image as ImageIcon
} from 'lucide-react';
import { assetService } from '@services/assetService';

const ClientAssetsList = ({ clientId }) => {
    const navigate = useNavigate();
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (clientId) {
            fetchClientAssets();
        }
    }, [clientId]);

    const fetchClientAssets = async () => {
        try {
            setLoading(true);
            const response = await assetService.clientAssets.getByClient(clientId);
            if (response.success && response.data) {
                setAssets(response.data);
            }
        } catch (error) {
            console.error('Error fetching client assets:', error);
            setError('Failed to load assets');
        } finally {
            setLoading(false);
        }
    };

    const getAssetIcon = (type) => {
        switch (type) {
            case 'VEHICLE': return Car;
            case 'LAND':
            case 'PROPERTY': return Home;
            case 'PHONE': return Smartphone;
            default: return Package;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'VERIFIED': return 'bg-green-100 text-green-800';
            case 'PENDING': return 'bg-yellow-100 text-yellow-800';
            case 'REJECTED': return 'bg-red-100 text-red-800';
            case 'UNDER_REVIEW': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'VERIFIED': return CheckCircle;
            case 'REJECTED': return XCircle;
            default: return Clock;
        }
    };

    const handleDeleteAsset = async (assetId) => {
        if (window.confirm('Are you sure you want to delete this asset?')) {
            try {
                await assetService.clientAssets.delete(assetId);
                fetchClientAssets();
            } catch (error) {
                console.error('Error deleting asset:', error);
            }
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                {error}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Client Assets ({assets.length})
                </h4>
                <button
                    onClick={() => navigate(`/clients/${clientId}/assets/new`)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    Add Asset
                </button>
            </div>

            {/* Assets Table */}
            {assets.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-2">No assets found for this client</p>
                    <button
                        onClick={() => navigate(`/clients/${clientId}/assets/new`)}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                        Add your first asset
                    </button>
                </div>
            ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Asset
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Company
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Documents
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                        {assets.map((asset) => {
                            const Icon = getAssetIcon(asset.assetType);
                            const StatusIcon = getStatusIcon(asset.status);

                            return (
                                <tr key={asset.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <Icon className="w-5 h-5 text-gray-600" />
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-900">{asset.requirementName}</div>
                                                <div className="text-xs text-gray-500">{asset.assetCode}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm text-gray-900">{asset.assetType}</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm text-gray-900">{asset.requirementName}</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-1 text-sm text-gray-900">
                                            <ImageIcon className="w-4 h-4 text-gray-400" />
                                            <span>{asset.documents?.length || 0}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 w-fit ${getStatusColor(asset.status)}`}>
                                                <StatusIcon className="w-3 h-3" />
                                                {asset.status}
                                            </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => navigate(`/assets/${asset.id}`)}
                                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                                                title="View asset"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => navigate(`/clients/${clientId}/assets/${asset.id}/edit`)}
                                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                                                title="Edit asset"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteAsset(asset.id)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                                title="Delete asset"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ClientAssetsList;
